import React from "react";
import StatusBadge from "../ui/StatusBadge";
import {
  formatDate,
  getCalculatieCloneTypeLabel,
  getAppliedBadgeMeta,
  getCalculatieClientName,
  getCalculatieContactName,
  getCalculatieDocumentNumber,
  getCalculatieOpnameTitle,
  getCalculatieSeriesCode,
  getClientName,
  getClientNumber,
  getProjectName,
  getProjectNumber,
  getSourceTypeLabel,
  toMoney,
} from "./utils";

export default function CalculatieOverviewTable({
  loading,
  calculaties,
  clients,
  selectedClientFilter,
  onClientFilterChange,
  onSelect,
  activeId,
  showFinancials = true,
}) {
  return (
    <section className="border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Calculatie-overzicht</h3>
          <p className="mt-1 text-sm text-slate-600">Scanbaar register op relatie, context en optionele projectkoppeling.</p>
        </div>
        <select
          value={selectedClientFilter}
          onChange={(e) => onClientFilterChange(e.target.value)}
          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
        >
          <option value="all">Alle relaties</option>
          {clients.map((client) => (
            <option key={client._id} value={client._id}>
              {getClientNumber(client)} - {getClientName(client)}
            </option>
          ))}
        </select>
      </div>

      {loading ? <p className="px-4 py-8 text-sm text-slate-500">Laden...</p> : null}
      {!loading && calculaties.length === 0 ? (
        <p className="px-4 py-8 text-sm text-slate-500">Nog geen calculaties gevonden.</p>
      ) : null}

      {!loading && calculaties.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2 font-semibold">Nr / calculatie</th>
                <th className="px-4 py-2 font-semibold">Relatie</th>
                <th className="px-4 py-2 font-semibold">Context</th>
                <th className="px-4 py-2 font-semibold">Project</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">Versie</th>
                <th className="px-4 py-2 font-semibold text-right">Verkoop</th>
                {showFinancials ? <th className="px-4 py-2 font-semibold text-right">Marge</th> : null}
                <th className="px-4 py-2 font-semibold">Projectbudget</th>
                <th className="px-4 py-2 font-semibold">Laatst gewijzigd</th>
                <th className="px-4 py-2 font-semibold">Referentie</th>
              </tr>
            </thead>
            <tbody>
              {calculaties.map((item) => {
                const project = item?.projectId || {};
                const appliedMeta = getAppliedBadgeMeta(item);
                const active = String(activeId || "") === String(item._id || "");
                const cloneTypeLabel = getCalculatieCloneTypeLabel(item?.cloneType);
                const derivedFromLabel = item?.derivedFromCalculatieId
                  ? getCalculatieDocumentNumber(item.derivedFromCalculatieId)
                  : "";
                return (
                  <tr
                    key={item._id}
                    className={`cursor-pointer border-b border-slate-100 transition hover:bg-slate-50 ${active ? "bg-slate-50" : "bg-white"}`}
                    onClick={() => onSelect(item)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{getCalculatieDocumentNumber(item)}</div>
                      <div className="text-slate-500">{item.title || "-"}</div>
                      <div className="text-xs text-slate-400">
                        {getCalculatieSeriesCode(item)}
                        {derivedFromLabel ? ` · van ${derivedFromLabel}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>{getCalculatieClientName(item)}</div>
                      <div className="text-xs text-slate-500">{getCalculatieContactName(item)}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>{getSourceTypeLabel(item?.sourceType)}</div>
                      <div className="text-xs text-slate-500">
                        {item?.sourceReference || getCalculatieOpnameTitle(item)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {item?.projectId ? `${getProjectNumber(project)} - ${getProjectName(project)}` : "-"}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={item.status} label={item.status || "-"} /></td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="font-medium text-slate-900">v{item.version || 1}</div>
                      <div className="text-xs text-slate-500">{cloneTypeLabel}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900">€ {toMoney(item?.totals?.totalSell)}</td>
                    {showFinancials ? (
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${Number(item?.totals?.margin || 0) < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                          € {toMoney(item?.totals?.margin)} ({Number(item?.totals?.marginPct || 0).toFixed(1)}%)
                        </span>
                      </td>
                    ) : null}
                    <td className="px-4 py-3"><StatusBadge tone={appliedMeta.tone} label={appliedMeta.label} /></td>
                    <td className="px-4 py-3 text-slate-700">{formatDate(item.updatedAt)}</td>
                    <td className="px-4 py-3 text-slate-500">
                      {item?.sourceReference || getCalculatieOpnameTitle(item) || "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
